import {
  adminMenuData,
  educatorMenuData,
  learnerMenuData
} from '@/constants/navbar'
import { Role } from '@prisma/client'
import { clsx, type ClassValue } from 'clsx'
import React from 'react'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const copyToClipboard = async (
  text: string,
  successMessage = 'Meeting link copied to clipboard'
): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)
        toast.success(successMessage, { closeButton: false })
        return true
      } catch {
        // Browsers may deny the async API while allowing the native fallback.
      }
    }

    const previousFocus = document.activeElement as HTMLElement | null
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    try {
      textArea.select()
      if (!document.execCommand('copy')) throw new Error('Clipboard unavailable')
    } finally {
      document.body.removeChild(textArea)
      previousFocus?.focus({ preventScroll: true })
    }

    toast.success(successMessage)
    return true
  } catch {
    toast.error('Failed to copy text')
    return false
  }
}

export const extractOriginFromUrl = (url: string | null | undefined) => {
  // Deprecated in favor of safeExtractOrigin; keep for backward-compatibility
  return safeExtractOrigin(url as string)
}

/**
 * Extract origin from a URL string without throwing.
 * Returns empty string when the URL is empty/relative/invalid.
 */
export const safeExtractOrigin = (url: string | null | undefined): string => {
  try {
    if (!url || typeof url !== 'string') return ''
    const trimmedUrl = url.trim()
    if (!trimmedUrl) return ''
    // Only absolute http(s) are accepted for origin extraction
    if (!/^https?:\/\//i.test(trimmedUrl)) return ''
    const u = new URL(trimmedUrl)
    return `${u.protocol}//${u.host}`
  } catch (error) {
    console.error('Invalid URL in safeExtractOrigin:', {
      url,
      error: error instanceof Error ? error.message : error
    })
    return ''
  }
}

export const safeCreateUrl = (
  base: string | undefined | null,
  path: string = ''
): URL | null => {
  if (!base || typeof base !== 'string' || base.trim() === '') {
    return null
  }

  try {
    let cleanBase = base.trim()

    // Validate base URL more thoroughly
    if (!cleanBase || cleanBase === 'undefined' || cleanBase === 'null') {
      return null
    }

    // If base lacks a scheme, default to https
    const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(cleanBase)
    if (!hasScheme) {
      cleanBase = `https://${cleanBase}`
    }

    // Additional validation before creating URL
    if (!isValidUrlString(cleanBase)) {
      return null
    }

    const result = new URL(path || '', cleanBase)
    return result
  } catch (err) {
    return null
  }
}

function isValidUrlString(url: string): boolean {
  try {
    // Basic format validation before using new URL()
    if (!url || typeof url !== 'string') return false
    if (url.length > 2048) return false // Reasonable URL length limit
    if (!url.match(/^(https?|wss?):\/\/.+/)) return false

    // Try creating URL to validate
    new URL(url)
    return true
  } catch {
    return false
  }
}

export const getAudioTrackSid = (trackPublications: Map<string, any>) => {
  for (const [key, publication] of trackPublications) {
    if (publication.kind === 'audio') {
      return key
    }
  }
  return null
}

export function isValidHostname(hostname: string): boolean {
  return Boolean(hostname && /^[a-zA-Z0-9.-]+(\:[0-9]+)?$/.test(hostname))
}

export function cloneSingleChild(
  children: React.ReactNode | React.ReactNode[],
  props?: Record<string, any>,
  key?: any
) {
  return React.Children.map(children, child => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child) && React.Children.only(children)) {
      if (child.props.class) {
        // make sure we retain classnames of both passed props and child
        props ??= {}
        props.class = clsx(child.props.class, props.class)
        props.style = { ...child.props.style, ...props.style }
      }
      return React.cloneElement(child, { ...props, key })
    }
    return child
  })
}

export const formatDateDivider = (timestamp: number) => {
  const date = new Date(timestamp)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  // Check if it's today
  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  }

  // Check if it's yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  }

  // Otherwise return formatted date
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const isDifferentDay = (timestamp1: number, timestamp2: number) => {
  const date1 = new Date(timestamp1)
  const date2 = new Date(timestamp2)
  return date1.toDateString() !== date2.toDateString()
}

export const getMenuData = (role: Role) => {
  switch (role) {
    case 'EDUCATOR':
      return educatorMenuData
    case 'LEARNER':
      return learnerMenuData
    case 'COMPANY_ADMIN':
    case 'SYSTEM_ADMIN':
      return adminMenuData
    default:
      return learnerMenuData
  }
}

export const getSafeOrigin = (fallbackHost?: string): string => {
  try {
    // Browser runtime
    if (typeof window !== 'undefined' && window.location?.origin) {
      const origin = window.location.origin
      return /^https?:\/\//.test(origin) ? origin : ''
    }

    // Server runtime: infer from env or fallback host
    const isProd = process.env.NODE_ENV === 'production'
    const protocol = isProd ? 'https' : 'http'

    const clean = (v?: string) => (v && v.trim() ? v.trim() : undefined)
    const envBase =
      clean(process.env.NEXTAUTH_URL) ||
      clean(process.env.NEXT_PUBLIC_APP_URL) ||
      clean(process.env.AUTH_URL) ||
      clean(process.env.VERCEL_URL)

    if (envBase) {
      // If envBase is a bare host, prefix protocol
      if (/^[^/:]+(\:\\d+)?$/.test(envBase)) {
        return `${protocol}://${envBase}`
      }
      return envBase.startsWith('http') ? envBase : `${protocol}://${envBase}`
    }

    if (fallbackHost) {
      return `${protocol}://${fallbackHost}`
    }

    // Last resort for local dev
    return `${protocol}://localhost:3000`
  } catch (error) {
    console.error(
      'Error getting safe origin:',
      error instanceof Error ? error.message : error
    )
    return ''
  }
}

export const safeRouterPush = (
  router: any,
  path: string,
  fallbackPath = '/dashboard'
) => {
  try {
    if (!router || typeof router.push !== 'function') {
      console.error('Invalid router provided to safeRouterPush')
      return false
    }

    if (!path || typeof path !== 'string' || path.trim() === '') {
      console.error('Invalid path provided to safeRouterPush:', path)
      return false
    }

    router.push(path.trim())
    return true
  } catch (error) {
    console.error(
      'Error in safeRouterPush:',
      error instanceof Error ? error.message : error
    )
    try {
      // Fallback to safe path
      router.push(fallbackPath)
    } catch (fallbackError) {
      console.error(
        'Fallback router push also failed:',
        fallbackError instanceof Error ? fallbackError.message : fallbackError
      )
      // Last resort - reload the page to dashboard
      if (typeof window !== 'undefined') {
        window.location.href = fallbackPath
      }
    }
    return false
  }
}

export const getRoleColor = (role: string) => {
  return role === 'EDUCATOR'
    ? 'border-purple-700/40 bg-purple-900/40 text-purple-200'
    : 'border-green-700/40 bg-green-900/40 text-green-200' 
}

export const getAvatarGradient = (role: string) => {
  return role === 'EDUCATOR'
    ? 'bg-gradient-to-br from-purple-500/80 to-purple-600/80'
    : 'bg-gradient-to-br from-green-500/80 to-green-600/80'
}

// Extract initials from full name

// ...existing code...
export const getInitials = (name: string) => {
  if (!name || typeof name !== 'string' || name.trim() === '') {
    return ''
  }

  return name
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2)
}

/**
 * Turns a camelCase column id ("meetingType") into readable words
 * ("meeting Type"). Table column ids are accessor keys, so without this the
 * column-toggle menu renders them raw. Also splits digit boundaries and
 * underscore/hyphen separators. Callers pair it with `capitalize`.
 */
export function columnLabel(id: string): string {
  return id
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
}
